// ============================================================
// TaxiCommercialCalculatorPage.tsx - חלק 1/4
// Imports + Interfaces + Constants
// ============================================================

import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { printCalculationDocument } from "../../../utils/printCalculationDocument";
import {
  calculatorService,
  CurrencyRate,
  ExplanationData,
  ApiResponse,
  saveCalculationWithNotes,
  getTaxiCommercialCalculation,
  TaxiCommercialCalculationResponse,
} from "../../../services/enduser/calculatorService";
import MotionCard, { MotionButton } from "../../shared/MotionCard";
import ExplanationTooltip from "../../shared/ExplanationTooltip";
import TaxYearNotice from "../../shared/TaxYearNotice";
import GreenRatingCalculatorModal from "../../shared/GreenRatingCalculatorModal";
import CalculatorIcon from "../../shared/CalculatorIcon";
import ActualCostCalculator, { ActualCostData } from "../../shared/ActualCostCalculator";

// ============================================================
// Interfaces
// ============================================================

interface FormData {
  vehicle_price_foreign: string;
  currency_type: string;
  exchange_rate: string;
  shipping_cost_foreign: string;
  insurance_cost_foreign: string;
  insurance_currency_type: string;
  insurance_exchange_rate: string;
  local_expenses_ils: string;
  engine_type: string;
  has_origin_declaration: boolean;
  green_rating_group: string;
  vehicle_weight_over_3500: boolean;
  manufacturing_date: string;
}

interface SafetyEquipmentData {
  airbagsCount: number;
  systems: {
    blindSpotDetection: boolean; // 1 - זיהוי כלי רכב ב"שטח מת"
    blindSpotCollisionPrevention: boolean; // 2 - אקטיבית למניעת התנגשות צד
    reverseBraking: boolean; // 3 - בלימה אוטומטית לאחור
    driverDistraction: boolean; // 4 - התרעה היסח דעת הנהג
    childAlert: boolean; // 5 - התרעה ילדים ברכב
    doorOpenWarning: boolean; // 6 - אזהרת פתיחת דלת
    adaptiveLighting: boolean; // 7 - תאורה אדפטיבית
    junctionEmergencyBraking: boolean; // 8 - בלימת חירום בצמתים
  };
}

interface GreenRatingGroup {
  id: number;
  group_number: number;
  group_display_name: string;
  co2_range_min: number;
  co2_range_max: number;
  discount_amount: number;
  is_active: boolean;
  sort_order: number;
}

interface CalculatorData {
  currency_rates: CurrencyRate[];
  explanations: Record<string, ExplanationData>;
  green_rating_groups?: GreenRatingGroup[];
}

interface SaveCalculationRequest {
  calculation_type: string;
  input_data: any;
  result_data: any;
  notes?: string;
}

// ============================================================
// Constants
// ============================================================

const ENGINE_TYPES = [
  "בנזין רגיל",
  "היברידי בנזין",
  "פלאג אין בנזין",
  "דיזל רגיל",
  "היברידי דיזל",
  "פלאג אין דיזל",
  "חשמלי",
] as const;

type EngineType = (typeof ENGINE_TYPES)[number];

// מפתחות השדות לטולטיפס הסבר
const STEP_KEYS = {
  manufacturing_date: "manufacturing_date",
  vehicle_price_foreign: "vehicle_price",
  currency_type: "currency_selection",
  exchange_rate: "exchange_rate",
  shipping_cost_foreign: "shipping_cost",
  insurance_cost_foreign: "insurance_cost",
  insurance_currency_type: "insurance_currency",
  insurance_exchange_rate: "insurance_exchange_rate",
  local_expenses_ils: "local_expenses",
  has_origin_declaration: "origin_declaration",
  engine_type: "taxi_engine_type", // ✅ שונה - ייחודי למוניות!
  weight_category: "weight_category",
  green_rating_group: "green_rating",
  target_consumer_price: "consumer_price_commercial", // ✅ שונה!
} as const;

// ציוני מערכות בטיחות - עדכון נובמבר 2025 (תקנות 2026)
const SAFETY_SYSTEMS_SCORES = {
  blindSpotDetection: 1.0, // מס' 1
  blindSpotCollisionPrevention: 2.0, // מס' 2
  reverseBraking: 1.0, // מס' 3
  driverDistraction: 1.0, // מס' 4
  childAlert: 1.5, // מס' 5
  doorOpenWarning: 1.5, // מס' 6
  adaptiveLighting: 1.0, // מס' 7
  junctionEmergencyBraking: 1.5, // מס' 8
} as const;

// טבלת רמות הנחה M1 (6 כריות מינימום)
const SAFETY_LEVELS = [
  { level: 0, minScore: 0, minAirbags: 0, discount: 0 },
  { level: 1, minScore: 1, minAirbags: 6, discount: 250 },
  { level: 2, minScore: 2, minAirbags: 6, discount: 600 },
  { level: 3, minScore: 3, minAirbags: 6, discount: 1000 },
  { level: 4, minScore: 4, minAirbags: 6, discount: 1600 },
  { level: 5, minScore: 5, minAirbags: 6, discount: 1850 },
  { level: 6, minScore: 6, minAirbags: 6, discount: 2050 },
  { level: 7, minScore: 8, minAirbags: 6, discount: 2250 },
  { level: 8, minScore: 10, minAirbags: 6, discount: 2400 },
] as const;
// ============================================================
// TaxiCommercialCalculatorPage.tsx - חלק 2/4
// State Management + useEffect + Helper Functions
// ============================================================

const SAFETY_LEVEL_TO_FEATURES: Record<number, { airbagsCount: number; systems: Record<string, boolean> }> = {
  0: { airbagsCount: 0, systems: { blindSpotDetection: false, blindSpotCollisionPrevention: false, reverseBraking: false, driverDistraction: false, childAlert: false, doorOpenWarning: false, adaptiveLighting: false, junctionEmergencyBraking: false } },
  1: { airbagsCount: 6, systems: { blindSpotDetection: true,  blindSpotCollisionPrevention: false, reverseBraking: false, driverDistraction: false, childAlert: false, doorOpenWarning: false, adaptiveLighting: false, junctionEmergencyBraking: false } },
  2: { airbagsCount: 6, systems: { blindSpotDetection: true,  blindSpotCollisionPrevention: false, reverseBraking: true,  driverDistraction: false, childAlert: false, doorOpenWarning: false, adaptiveLighting: false, junctionEmergencyBraking: false } },
  3: { airbagsCount: 6, systems: { blindSpotDetection: true,  blindSpotCollisionPrevention: false, reverseBraking: true,  driverDistraction: true,  childAlert: false, doorOpenWarning: false, adaptiveLighting: false, junctionEmergencyBraking: false } },
  4: { airbagsCount: 6, systems: { blindSpotDetection: true,  blindSpotCollisionPrevention: false, reverseBraking: true,  driverDistraction: true,  childAlert: false, doorOpenWarning: false, adaptiveLighting: true,  junctionEmergencyBraking: false } },
  5: { airbagsCount: 6, systems: { blindSpotDetection: true,  blindSpotCollisionPrevention: false, reverseBraking: true,  driverDistraction: true,  childAlert: true,  doorOpenWarning: false, adaptiveLighting: true,  junctionEmergencyBraking: false } },
  6: { airbagsCount: 6, systems: { blindSpotDetection: true,  blindSpotCollisionPrevention: false, reverseBraking: true,  driverDistraction: true,  childAlert: true,  doorOpenWarning: true,  adaptiveLighting: true,  junctionEmergencyBraking: false } },
  7: { airbagsCount: 6, systems: { blindSpotDetection: true,  blindSpotCollisionPrevention: false, reverseBraking: true,  driverDistraction: true,  childAlert: true,  doorOpenWarning: true,  adaptiveLighting: true,  junctionEmergencyBraking: true  } },
  8: { airbagsCount: 6, systems: { blindSpotDetection: true,  blindSpotCollisionPrevention: true,  reverseBraking: true,  driverDistraction: true,  childAlert: true,  doorOpenWarning: true,  adaptiveLighting: true,  junctionEmergencyBraking: true  } },
};

const SAFETY_LEVEL_DISCOUNTS: Record<number, number> = {
  0: 0, 1: 250, 2: 600, 3: 1000, 4: 1600, 5: 1850, 6: 2050, 7: 2250, 8: 2400,
};

const TaxiCommercialCalculatorPage: React.FC = () => {
  const navigate = useNavigate();

  // ============================================================
  // State Management
  // ============================================================

  const [currentStep, setCurrentStep] = useState(1);

  // נתוני טופס
  const [formData, setFormData] = useState<FormData>({
    vehicle_price_foreign: "",
    currency_type: "",
    exchange_rate: "",
    shipping_cost_foreign: "",
    insurance_cost_foreign: "",
    insurance_currency_type: "",
    insurance_exchange_rate: "",
    local_expenses_ils: "",
    engine_type: "",
    has_origin_declaration: false,
    green_rating_group: "17",
    vehicle_weight_over_3500: false,
    manufacturing_date: "",
  });

  // אבזור בטיחותי M1 - בשלב 1! (עדכון נובמבר 2025 - תקנות 2026)
  const [safetyEquipmentData, setSafetyEquipmentData] =
    useState<SafetyEquipmentData>({
      airbagsCount: 0,
      systems: {
        blindSpotDetection: false,
        blindSpotCollisionPrevention: false,
        reverseBraking: false,
        driverDistraction: false,
        childAlert: false,
        doorOpenWarning: false,
        adaptiveLighting: false,
        junctionEmergencyBraking: false,
      },
    });

  // נתוני עזר
  const [currencies, setCurrencies] = useState<CurrencyRate[]>([]);
  const [explanations, setExplanations] = useState<
    Record<string, ExplanationData>
  >({});
  const [greenRatingGroups, setGreenRatingGroups] = useState<
    GreenRatingGroup[]
  >([]);

  // מצב חישוב
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [calculationResult, setCalculationResult] =
    useState<TaxiCommercialCalculationResponse | null>(null);

  // ref לאזור התוצאה — מקור לשכפול בהדפסה החדשה (גרסת פיתוח)
  const printRef = useRef<HTMLDivElement>(null);

  // שמירה
  const [userNotes, setUserNotes] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  // מודאל ציון ירוק
  const [showGreenRatingModal, setShowGreenRatingModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [commercialActualCostData, setCommercialActualCostData] = useState<ActualCostData | null>(null);
  const [safetyInputMode, setSafetyInputMode] = useState<"select" | "calculator">("select");
  const [selectedSafetyLevel, setSelectedSafetyLevel] = useState<number>(0);
  // ============================================================
  // useEffect Hooks
  // ============================================================

  // טעינת נתוני מחשבון בטעינה ראשונית
  useEffect(() => {
    loadCalculatorData();
  }, []);

  const loadCalculatorData = async () => {
    setIsLoading(true);
    try {
      const response: ApiResponse<CalculatorData> =
        await calculatorService.getCalculatorData();

      if (response.res === "OK" && response.data) {
        setCurrencies(response.data.currency_rates || []);
        setExplanations(response.data.explanations || {});
        setGreenRatingGroups(response.data.green_rating_groups || []);
      } else {
        throw new Error(response.msg || "שגיאה בטעינת נתוני המחשבון");
      }
    } catch (error) {
      console.error("שגיאה בטעינה:", error);
      setError("שגיאה בטעינת נתוני המחשבון");
    } finally {
      setIsLoading(false);
    }
  };

  // עדכון שער חליפין אוטומטי למטבע הרכב
  useEffect(() => {
    if (formData.currency_type && currencies.length > 0) {
      const selectedCurrency = currencies.find(
        (c) => c.currency_code === formData.currency_type
      );
      if (selectedCurrency) {
        const customsRate = Number(selectedCurrency.customs_rate) || 0;
        setFormData((prev) => ({
          ...prev,
          exchange_rate: customsRate.toFixed(4),
          insurance_currency_type: selectedCurrency.currency_code,
          insurance_exchange_rate: customsRate.toFixed(4),
        }));
      }
    }
  }, [formData.currency_type, currencies]);

  // עדכון שער חליפין אוטומטי למטבע הביטוח
  useEffect(() => {
    if (formData.insurance_currency_type && currencies.length > 0) {
      const selectedInsuranceCurrency = currencies.find(
        (c) => c.currency_code === formData.insurance_currency_type
      );
      if (selectedInsuranceCurrency) {
        const insuranceCustomsRate =
          Number(selectedInsuranceCurrency.customs_rate) || 0;
        setFormData((prev) => ({
          ...prev,
          insurance_exchange_rate: insuranceCustomsRate.toFixed(4),
        }));
      }
    }
  }, [formData.insurance_currency_type, currencies]);

  // ============================================================
  // Helper Functions
  // ============================================================

  // טיפול בשינוי שדות
  const handleInputChange = (
    field: keyof FormData,
    value: string | boolean
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) setError("");
  };

  // טיפול בשינוי מספרים (למניעת אפסים מוזרים)
  const handleNumberInputChange = (field: keyof FormData, value: string) => {
    const cleanValue = value.replace(/^0+/, "") || "0";
    handleInputChange(field, cleanValue);
  };

  // בדיקת תאריך ייצור - מונית חייבת להיות בת פחות מ-24 חודשים
  const validateManufacturingDate = (dateString: string): boolean => {
    const manufacturingDate = new Date(dateString);
    const now = new Date();
    const yearsDifference = now.getFullYear() - manufacturingDate.getFullYear();
    const monthsDifference = now.getMonth() - manufacturingDate.getMonth();
    const totalMonthsDiff = yearsDifference * 12 + monthsDifference;
    return totalMonthsDiff <= 24;
  };

  // הגדרת תאריך מינימלי - 24 חודשים אחורה מהיום
  const getMinManufacturingDate = (): string => {
    const today = new Date();
    const twentyFourMonthsAgo = new Date(
      today.getFullYear() - 2,
      today.getMonth(),
      today.getDate()
    );
    return twentyFourMonthsAgo.toISOString().split("T")[0];
  };

  // חישוב ציון אבזור בטיחותי
  const calculateSafetyScore = (): number => {
    return Object.entries(safetyEquipmentData.systems)
      .filter(([_, enabled]) => enabled)
      .reduce((sum, [systemKey, _]) => {
        return (
          sum +
          (SAFETY_SYSTEMS_SCORES[
            systemKey as keyof typeof SAFETY_SYSTEMS_SCORES
          ] || 0)
        );
      }, 0);
  };

  // קביעת רמת אבזור
  const calculateSafetyLevel = (): number => {
    const score = calculateSafetyScore();
    const airbagsCount = safetyEquipmentData.airbagsCount;

    const eligibleLevels = SAFETY_LEVELS.filter(
      (level) => score >= level.minScore && airbagsCount >= level.minAirbags
    );

    if (eligibleLevels.length > 0) {
      return eligibleLevels[eligibleLevels.length - 1].level;
    }
    return 0;
  };

  // חישוב הנחת אבזור
  const calculateSafetyDiscount = (): number => {
    const level = calculateSafetyLevel();
    const levelData = SAFETY_LEVELS.find((l) => l.level === level);
    return levelData ? levelData.discount : 0;
  };

  // טיפול בשינוי תכונות אבזור
  const handleSafetyFeatureChange = (
    feature: keyof SafetyEquipmentData["systems"]
  ) => {
    setSafetyEquipmentData((prev) => ({
      ...prev,
      systems: {
        ...prev.systems,
        [feature]: !prev.systems[feature],
      },
    }));
  };

  // טיפול בשינוי מספר כריות
  const handleAirbagsChange = (value: string) => {
    const count = parseInt(value) || 0;
    setSafetyEquipmentData((prev) => ({
      ...prev,
      airbagsCount: count,
    }));
  };

  // טיפול בבחירת קבוצת ציון ירוק
  const handleGreenRatingGroupSelect = (groupNumber: number) => {
    handleInputChange("green_rating_group", groupNumber.toString());
    setShowGreenRatingModal(false);
  };

  // וולידציה שלב 1
  const validateStep1 = (): boolean => {
    if (!termsAccepted) {
      setError("יש לאשר את תנאי השימוש ומדיניות הפרטיות לפני ביצוע חישוב");
      return false;
    }
    // בדיקת תאריך ייצור
    if (!formData.manufacturing_date) {
      setError("נא להזין תאריך ייצור");
      return false;
    }

    if (!validateManufacturingDate(formData.manufacturing_date)) {
      setError("מונית חייבת להיות בת פחות מ-24 חודשים");
      return false;
    }

    // בדיקת שדות חובה
    if (!formData.vehicle_price_foreign) {
      setError("נא להזין ערך מונית");
      return false;
    }

    if (!formData.currency_type) {
      setError("נא לבחור סוג מטבע");
      return false;
    }

    if (!formData.shipping_cost_foreign) {
      setError("נא להזין עלות הובלה");
      return false;
    }
    if (!formData.local_expenses_ils && formData.local_expenses_ils !== "0") {
      setError("נא להזין הוצאות מקומיות (ניתן להזין 0)");
      return false;
    }
    if (!formData.engine_type) {
      setError("נא לבחור סוג מנוע");
      return false;
    }

    return true;
  };
  // ============================================================
  // TaxiCommercialCalculatorPage.tsx - חלק 3/4
  // Calculation + Save + Navigation Functions
  // ============================================================

  // ============================================================
  // Main Actions
  // ============================================================

  // מעבר לשלב הבא
  const handleNextStep = () => {
    if (currentStep === 1 && !validateStep1()) {
      return;
    }

    if (currentStep === 1) {
      // בצע חישוב
      performCalculation();
    }
  };

  // ביצוע חישוב
  const performCalculation = async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await getTaxiCommercialCalculation({
        vehicle_price_foreign: parseFloat(formData.vehicle_price_foreign) || 0,
        currency_type: formData.currency_type,
        exchange_rate: parseFloat(formData.exchange_rate) || 0,
        shipping_cost_foreign: parseFloat(formData.shipping_cost_foreign) || 0,
        insurance_cost_foreign:
          parseFloat(formData.insurance_cost_foreign) || 0,
        insurance_currency_type:
          formData.insurance_currency_type || formData.currency_type,
        insurance_exchange_rate:
          parseFloat(formData.insurance_exchange_rate) || 0,
        local_expenses_ils: parseFloat(formData.local_expenses_ils) || 0,
        engine_type: formData.engine_type as EngineType,
        has_origin_declaration: formData.has_origin_declaration,
        green_rating_group:
          formData.engine_type === "חשמלי"
            ? 1
            : formData.green_rating_group
            ? parseInt(formData.green_rating_group)
            : undefined,
        vehicle_weight_over_3500: formData.vehicle_weight_over_3500,
        // שליחת אבזור בטיחותי רק אם רכב רגיל (≤ 3500 ק"ג)
        safety_features: !formData.vehicle_weight_over_3500
          ? (safetyInputMode === "select"
              ? SAFETY_LEVEL_TO_FEATURES[selectedSafetyLevel] as SafetyEquipmentData
              : safetyEquipmentData)
          : undefined,
      });

      if (response.res === "OK" && response.data) {
        setCalculationResult(response.data);
        setCurrentStep(2);
      } else {
        setError(response.msg || "שגיאה בחישוב");
      }
    } catch (error) {
      console.error("שגיאה בחישוב:", error);
      setError("שגיאה בחישוב המונית");
    } finally {
      setIsLoading(false);
    }
  };

  // שמירת חישוב עם הערות
  const handleSaveCalculation = async () => {
    if (!calculationResult) return;

    setIsSaving(true);
    setError("");

    try {
      const saveRequest: SaveCalculationRequest = {
        calculation_type: "taxi_vehicle_commercial",
        input_data: calculationResult.input_data,
        result_data: {
          ...calculationResult,
          safety_equipment: calculationResult.safety_equipment,
          commercial_actual_cost_data: commercialActualCostData,
        },
        notes: userNotes.trim(),
      };

      const response = await saveCalculationWithNotes(saveRequest);

      if (response.res === "OK") {
        setIsSaved(true);
        setTimeout(() => {
          navigate("/enduser/dashboard");
        }, 1500);
      } else {
        setError(response.msg || "שגיאה בשמירת החישוב");
      }
    } catch (error) {
      console.error("שגיאה בשמירה:", error);
      setError("שגיאה בשמירת החישוב");
    } finally {
      setIsSaving(false);
    }
  };

  // הדפסה — מסמך נקי דרך המודול (דסקטופ: iframe מבודד, מובייל: טאב חדש)
  const handlePrint = () => {
    printCalculationDocument(printRef.current, {
      calculatorName: "מחשבון מוניות - ייבוא מסחרי",
      calculationDate: new Date().toLocaleDateString("he-IL"),
      variant: "calculator",
    });
  };

  // חזרה
  const handleGoBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate("/enduser/calculators/commercial");
    }
  };

  // ============================================================
  // UI Helper Functions
  // ============================================================

  // פורמט מטבע
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("he-IL", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // פורמט אחוז
  // const formatPercentage = (value: number): string => {
  //   return `${value.toFixed(2)}%`;
  // };

  // שם מטבע בעברית
  //   const getCurrencyName = (code: string): string => {
  //     const currency = currencies.find(c => c.currency_code === code);
  //     return currency?.currency_name_hebrew || code;
  //   };

  // תיאור מנוע בעברית
  //   const getEngineDescription = (engineType: string): string => {
  //     const descriptions: Record<string, string> = {
  //       "בנזין רגיל": "מנוע בנזין רגיל",
  //       "היברידי בנזין": "מנוע היברידי בנזין",
  //       "פלאג אין בנזין": "מנוע פלאג-אין היברידי בנזין",
  //       "דיזל רגיל": "מנוע דיזל רגיל",
  //       "היברידי דיזל": "מנוע היברידי דיזל",
  //       "פלאג אין דיזל": "מנוע פלאג-אין היברידי דיזל",
  //       "חשמלי": "מנוע חשמלי"
  //     };
  //     return descriptions[engineType] || engineType;
  //   };

  // קבלת שם קבוצת ציון ירוק
  const getGreenRatingGroupName = (groupNumber: number): string => {
    const group = greenRatingGroups.find((g) => g.group_number === groupNumber);
    return group?.group_display_name || `קבוצה ${groupNumber}`;
  };

  // צבע לפי רמת אבזור
  const getSafetyLevelColor = (level: number): string => {
    if (level === 0) return "text-gray-600";
    if (level <= 2) return "text-yellow-600";
    if (level <= 5) return "text-blue-600";
    return "text-green-600";
  };

  // איקון לפי רמת אבזור
  const getSafetyLevelIcon = (level: number): string => {
    if (level === 0) return "❌";
    if (level <= 2) return "⚠️";
    if (level <= 5) return "✅";
    return "🏆";
  };
  // ============================================================
  // TaxiCommercialCalculatorPage.tsx - חלק 4/4
  // Render Functions (UI)
  // ============================================================

  // ============================================================
  // Render Step 1 - טופס קלט
  // ============================================================

  const renderStep1 = () => (
    <MotionCard className="p-8">
      <h2 className="text-xl font-bold mb-6 text-center">
        חישוב מיסים למונית - ייבוא מסחרי
      </h2>

      <div className="space-y-6">
        <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="terms-calculator"
              checked={termsAccepted}
              onChange={(e) => {
                setTermsAccepted(e.target.checked);
                setError("");
              }}
              className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label
              htmlFor="terms-calculator"
              className="text-sm text-gray-700 flex-1"
            >
              קראתי ואני מאשר/ת את{" "}
              <Link
                to="/terms-and-privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline font-semibold"
              >
                תנאי השימוש ומדיניות הפרטיות
              </Link>{" "}
              *
            </label>
          </div>
        </div>
        {/* תאריך ייצור */}
        <div>
          <label
            htmlFor="manufacturing_date"
            className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"
          >
            תאריך ייצור <span className="text-red-500">*</span>
            <span className="sr-only">(שדה חובה)</span>
            <ExplanationTooltip
              stepKey={STEP_KEYS.manufacturing_date}
              explanations={explanations}
            />
          </label>
          <input
            id="manufacturing_date"
            type="date"
            aria-required="true"
            value={formData.manufacturing_date}
            onChange={(e) =>
              handleInputChange("manufacturing_date", e.target.value)
            }
            min={getMinManufacturingDate()}
            max={new Date().toISOString().split("T")[0]}
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
          />
          {/* <p className="text-xs text-gray-500 mt-1">
            מונית חייבת להיות בת פחות מ-24 חודשים
          </p> */}
          <p className="text-xs text-orange-800 mt-1 font-medium">
            ⚠️ יש לעקוב אחר הנחיות משרד התחבורה המתעדכנות מעת לעת
          </p>
        </div>

        {/* משקל הרכב */}
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center mb-2">
            <label className="flex items-center font-medium text-yellow-800">
              משקל המונית מעל 3.5 טון?
              {/* <ExplanationTooltip
                stepKey={STEP_KEYS.vehicle_weight_over_3500}
                explanations={explanations}
              /> */}
            </label>
          </div>
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="weight"
                checked={!formData.vehicle_weight_over_3500}
                onChange={() =>
                  handleInputChange("vehicle_weight_over_3500", false)
                }
                className="ml-2"
              />
              עד 3.5 טון
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="weight"
                checked={formData.vehicle_weight_over_3500}
                onChange={() =>
                  handleInputChange("vehicle_weight_over_3500", true)
                }
                className="ml-2"
              />
              מעל 3.5 טון
            </label>
          </div>
          {formData.vehicle_weight_over_3500 && (
            <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-blue-700 text-sm">
              ℹ️ רכב מעל 3.5 טון: פטור ממס קניה
            </div>
          )}
        </div>

        {/* ערך מונית */}
        <div>
          <label
            htmlFor="vehicle_price_foreign"
            className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"
          >
            ערך מונית <span className="text-red-500">*</span>
            <span className="sr-only">(שדה חובה)</span>
            <ExplanationTooltip
              stepKey={STEP_KEYS.vehicle_price_foreign}
              explanations={explanations}
            />
          </label>
          <input
            id="vehicle_price_foreign"
            type="number"
            aria-required="true"
            min="0"
            value={formData.vehicle_price_foreign}
            onChange={(e) =>
              handleNumberInputChange("vehicle_price_foreign", e.target.value)
            }
            placeholder="0"
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* מטבע */}
        <div>
          <label
            htmlFor="currency_type"
            className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"
          >
            סוג מטבע <span className="text-red-500">*</span>
            <span className="sr-only">(שדה חובה)</span>
            <ExplanationTooltip
              stepKey={STEP_KEYS.currency_type}
              explanations={explanations}
            />
          </label>
          <select
            id="currency_type"
            aria-required="true"
            value={formData.currency_type}
            onChange={(e) => handleInputChange("currency_type", e.target.value)}
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">בחר מטבע</option>
            {currencies.map((curr) => (
              <option key={curr.currency_code} value={curr.currency_code}>
                {curr.currency_name_hebrew} ({curr.currency_code})
              </option>
            ))}
          </select>
        </div>

        {/* שער חליפין */}
        <div>
          <label
            htmlFor="exchange_rate"
            className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"
          >
            שער חליפין <span className="text-red-500">*</span>
            <span className="sr-only">(שדה חובה)</span>
            <ExplanationTooltip
              stepKey={STEP_KEYS.exchange_rate}
              explanations={explanations}
            />
          </label>
          <input
            id="exchange_rate"
            type="number"
            aria-required="true"
            min="0"
            step="0.0001"
            value={formData.exchange_rate}
            onChange={(e) => handleInputChange("exchange_rate", e.target.value)}
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* עלות הובלה */}
        <div>
          <label
            htmlFor="shipping_cost_foreign"
            className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"
          >
            עלות הובלה <span className="text-red-500">*</span>
            <span className="sr-only">(שדה חובה)</span>
            <ExplanationTooltip
              stepKey={STEP_KEYS.shipping_cost_foreign}
              explanations={explanations}
            />
          </label>
          <input
            id="shipping_cost_foreign"
            type="number"
            aria-required="true"
            min="0"
            value={formData.shipping_cost_foreign}
            onChange={(e) =>
              handleNumberInputChange("shipping_cost_foreign", e.target.value)
            }
            placeholder="0"
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* עלות ביטוח */}
        <div>
          <label
            htmlFor="insurance_cost_foreign"
            className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"
          >
            עלות ביטוח (אופציונלי)
            <ExplanationTooltip
              stepKey={STEP_KEYS.insurance_cost_foreign}
              explanations={explanations}
            />
          </label>
          <input
            type="number"
            min="0"
            value={formData.insurance_cost_foreign}
            onChange={(e) =>
              handleNumberInputChange("insurance_cost_foreign", e.target.value)
            }
            placeholder="0"
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        {/* מטבע ביטוח */}
        <div>
          <label
            htmlFor="insurance_currency_type"
            className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"
          >
            מטבע ביטוח (אופציונלי)
            <ExplanationTooltip
              stepKey={STEP_KEYS.insurance_currency_type}
              explanations={explanations}
            />
          </label>
          <select
            id="insurance_currency_type"
            value={formData.insurance_currency_type}
            onChange={(e) =>
              handleInputChange("insurance_currency_type", e.target.value)
            }
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">בחר מטבע</option>
            {currencies.map((curr) => (
              <option key={curr.currency_code} value={curr.currency_code}>
                {curr.currency_name_hebrew} ({curr.currency_code})
              </option>
            ))}
          </select>
        </div>

        {/* שער חליפין ביטוח */}
        <div>
          <label
            htmlFor="insurance_exchange_rate"
            className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"
          >
            שער חליפין ביטוח (אופציונלי)
            <ExplanationTooltip
              stepKey={STEP_KEYS.insurance_exchange_rate}
              explanations={explanations}
            />
          </label>
          <input
            id="insurance_exchange_rate"
            type="number"
            min="0"
            step="0.0001"
            value={formData.insurance_exchange_rate}
            onChange={(e) =>
              handleInputChange("insurance_exchange_rate", e.target.value)
            }
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        {/* הוצאות מקומיות */}
        <div>
          <label
            htmlFor="local_expenses_ils"
            className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"
          >
            הוצאות מקומיות (₪) <span className="text-red-500">*</span>
            <span className="sr-only">(שדה חובה)</span>
            <ExplanationTooltip
              stepKey={STEP_KEYS.local_expenses_ils}
              explanations={explanations}
            />
          </label>
          <input
            id="local_expenses_ils"
            type="number"
            aria-required="true"
            min="0"
            value={formData.local_expenses_ils}
            onChange={(e) =>
              handleNumberInputChange("local_expenses_ils", e.target.value)
            }
            placeholder="0"
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* סוג מנוע */}
        <div>
          <label
            htmlFor="engine_type"
            className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"
          >
            סוג מנוע <span className="text-red-500">*</span>
            <span className="sr-only">(שדה חובה)</span>
            <ExplanationTooltip
              stepKey={STEP_KEYS.engine_type}
              explanations={explanations}
            />
          </label>
          <select
            id="engine_type"
            aria-required="true"
            value={formData.engine_type}
            onChange={(e) => handleInputChange("engine_type", e.target.value)}
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">בחר סוג מנוע</option>
            {ENGINE_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        {/* הצהרת מקור */}
        <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
          <input
            type="checkbox"
            id="origin-declaration"
            checked={formData.has_origin_declaration}
            onChange={(e) =>
              handleInputChange("has_origin_declaration", e.target.checked)
            }
            className="w-5 h-5 text-blue-600"
          />
          <label
            htmlFor="origin-declaration"
            className="text-sm font-medium text-gray-700 flex items-center gap-2"
          >
            יש הצהרת מקור
            <ExplanationTooltip
              stepKey={STEP_KEYS.has_origin_declaration}
              explanations={explanations}
            />
          </label>
        </div>

        {/* ציון ירוק - רק אם רכב רגיל (≤ 3500 ק"ג) ולא חשמלי */}
        {!formData.vehicle_weight_over_3500 &&
          formData.engine_type &&
          formData.engine_type !== "חשמלי" && (
            <div>
              <label
                htmlFor="green_rating_group"
                className="flex items-center mb-2 font-medium"
              >
                קבוצת ציון ירוק
                <ExplanationTooltip
                  stepKey={STEP_KEYS.green_rating_group}
                  explanations={explanations}
                />
                <CalculatorIcon
                  onClick={() => setShowGreenRatingModal(true)}
                  tooltip="למחשבון ציון ירוק"
                />
              </label>
              <select
                id="green_rating_group"
                value={formData.green_rating_group}
                onChange={(e) =>
                  handleInputChange("green_rating_group", e.target.value)
                }
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">בחר קבוצת ציון ירוק</option>
                {greenRatingGroups.length > 0
                  ? greenRatingGroups
                      .filter((group: GreenRatingGroup) => group.is_active)
                      .sort(
                        (a: GreenRatingGroup, b: GreenRatingGroup) =>
                          a.group_number - b.group_number
                      )
                      .map((group: GreenRatingGroup) => (
                        <option
                          key={group.group_number}
                          value={group.group_number.toString()}
                        >
                          קבוצה {group.group_display_name}
                        </option>
                      ))
                  : // Fallback למקרה שהנתונים לא נטענו
                    Array.from({ length: 17 }, (_, i: number) => {
                      const groupNum = i + 1;
                      let displayName: string;
                      if (groupNum <= 14) {
                        displayName = groupNum.toString();
                      } else if (groupNum === 15) {
                        displayName = "15א";
                      } else if (groupNum === 16) {
                        displayName = "15ב";
                      } else {
                        displayName = "15ג";
                      }
                      return (
                        <option key={groupNum} value={groupNum.toString()}>
                          קבוצה {displayName}
                        </option>
                      );
                    })}
              </select>
              <div className="mt-1 text-sm text-gray-500">
                הנחת ציון ירוק תוגבל לגובה מס הקניה
              </div>
            </div>
          )}

        {formData.engine_type === "חשמלי" &&
          !formData.vehicle_weight_over_3500 && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">
                ⚡ מנוע חשמלי - ציון ירוק קבוצה 1 יוחל אוטומטית
              </p>
            </div>
          )}

        {/* {formData.engine_type === "חשמלי" && !formData.vehicle_weight_over_3500 && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-800">
              ⚡ מנוע חשמלי - ציון ירוק קבוצה 1 יוחל אוטומטית
            </p>
          </div>
        )} */}

        {/* אבזור בטיחותי - רק אם רכב רגיל (≤ 3500 ק"ג) */}
        {!formData.vehicle_weight_over_3500 && (
          <div className="border-t pt-6">
            <h3 className="text-lg font-bold mb-2 text-center">
              🛡️ אבזור בטיחותי M1
            </h3>
            <p className="text-sm text-gray-600 mb-4 text-center">
              הנחה אפשרית עד 2,400 ש"ח לפי רמת אבזור.
            </p>

            {/* מצב בחירה מהירה */}
            {safetyInputMode === "select" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  בחר רמת אבזור בטיחותי (0–8)
                </label>
                <select
                  value={selectedSafetyLevel}
                  onChange={(e) => setSelectedSafetyLevel(parseInt(e.target.value))}
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-400 mb-3"
                >
                  <option value="0">רמה 0 — ללא הנחה (0 ₪)</option>
                  <option value="1">רמה 1 — הנחה 250 ₪</option>
                  <option value="2">רמה 2 — הנחה 600 ₪</option>
                  <option value="3">רמה 3 — הנחה 1,000 ₪</option>
                  <option value="4">רמה 4 — הנחה 1,600 ₪</option>
                  <option value="5">רמה 5 — הנחה 1,850 ₪</option>
                  <option value="6">רמה 6 — הנחה 2,050 ₪</option>
                  <option value="7">רמה 7 — הנחה 2,250 ₪</option>
                  <option value="8">רמה 8 — הנחה 2,400 ₪</option>
                </select>
                <button
                  type="button"
                  onClick={() => setSafetyInputMode("calculator")}
                  className="text-sm text-orange-600 underline hover:text-orange-800"
                >
                  לא יודע את הרמה? פתח מחשבון אבזור בטיחותי
                </button>
                {selectedSafetyLevel > 0 && (
                  <div className="mt-3 p-3 bg-orange-50 border border-orange-300 rounded-lg text-sm text-orange-800 font-medium">
                    רמת אבזור: {selectedSafetyLevel} | הנחה צפויה: {SAFETY_LEVEL_DISCOUNTS[selectedSafetyLevel].toLocaleString()} ₪
                  </div>
                )}
              </div>
            )}

            {/* מצב מחשבון מפורט */}
            {safetyInputMode === "calculator" && (
              <div>
                <div className="mb-6">
                  <label htmlFor="airbags_count" className="block text-sm font-medium text-gray-700 mb-2">
                    מספר כריות אוויר
                  </label>
                  <input
                    id="airbags_count"
                    type="number" min="0" max="20"
                    value={safetyEquipmentData.airbagsCount}
                    onChange={(e) => handleAirbagsChange(e.target.value)}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  {safetyEquipmentData.airbagsCount < 6 && safetyEquipmentData.airbagsCount > 0 && (
                    <p className="text-xs text-red-600 mt-1">⚠️ נדרש מינימום 6 כריות אוויר להנחה (M1)</p>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                  {Object.entries(safetyEquipmentData.systems).map(([key, value]) => {
                    const labels: Record<string, { text: string; score: number }> = {
                      blindSpotDetection: { text: 'זיהוי כלי רכב ב"שטח מת"', score: 1.0 },
                      blindSpotCollisionPrevention: { text: 'אקטיבית למניעת התנגשות צד ב"שטח מת"', score: 2.0 },
                      reverseBraking: { text: "בלימה אוטומטית בנסיעה לאחור", score: 1.0 },
                      driverDistraction: { text: "התרעה במצב היסח דעת הנהג", score: 1.0 },
                      childAlert: { text: "התרעה על השארת ילדים ברכב", score: 1.5 },
                      doorOpenWarning: { text: "אזהרת פתיחת דלת", score: 1.5 },
                      adaptiveLighting: { text: "תאורה אדפטיבית/שליטה באורות גבוהים", score: 1.0 },
                      junctionEmergencyBraking: { text: "בלימת חירום לתנועה חוצה בצמתים", score: 1.5 },
                    };
                    const label = labels[key as keyof typeof labels];
                    return (
                      <label key={key} className="flex items-start gap-3 p-3 border rounded-lg hover:bg-orange-50 cursor-pointer transition-colors">
                        <input type="checkbox" checked={value}
                          onChange={() => handleSafetyFeatureChange(key as keyof SafetyEquipmentData["systems"])}
                          className="mt-1 w-4 h-4 text-blue-600" />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-900">{label.text}</span>
                          <span className="text-xs text-gray-500 block">נקודות: {label.score}</span>
                        </div>
                      </label>
                    );
                  })}
                </div>
                {/* סיכום מחשבון */}
                <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg mb-3">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-xs text-gray-600">ציון מערכות</p>
                      <p className="text-xl font-bold text-blue-600">{calculateSafetyScore().toFixed(1)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">רמת הנחה</p>
                      <p className="text-xl font-bold text-green-600">רמה {calculateSafetyLevel()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">הנחה צפויה</p>
                      <p className="text-xl font-bold text-purple-600">{calculateSafetyDiscount().toLocaleString()} ₪</p>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSafetyInputMode("select")}
                  className="text-sm text-orange-600 underline hover:text-orange-800"
                >
                  ← חזור לבחירה מהירה
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {/* הודעת שגיאה */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg">
          ❌ {error}
        </div>
      )}
      {/* כפתורים */}
      <div className="flex gap-4 mt-8">
        <MotionButton
          onClick={handleGoBack}
          className="flex-1 bg-gray-500 hover:bg-gray-600 text-white"
        >
          ← חזור
        </MotionButton>
        <MotionButton
          onClick={handleNextStep}
          disabled={isLoading}
          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
        >
          {isLoading ? "מחשב..." : "המשך לתוצאות →"}
        </MotionButton>
      </div>
    </MotionCard>
  );

  // ============================================================
  // Render Step 2 - תוצאות
  // ============================================================

  const renderStep2 = () => {
    if (!calculationResult) return null;

    const {
      input_data,
      intermediate_calculations,
      safety_equipment,
      final_results,
      // calculation_info,
    } = calculationResult;
    const isHeavy = input_data.is_heavy_vehicle;

    return (
      <div className="space-y-6">
        {/* אזור התוצאה הניתן להדפסה (לא כולל "דייק לי" וכרטיס השמירה) */}
        <div ref={printRef} className="space-y-6">
        <TaxYearNotice />
        {/* ============================================================ */}
        {/* כרטיס נתוני קלט */}
        {/* ============================================================ */}
        <MotionCard className="p-6">
          <h3 className="font-bold text-lg mb-4 text-blue-800">
            📋 נתוני הקלט שהוזנו:
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm bg-blue-50 p-4 rounded-lg">
            <div>
              <strong>תאריך ייצור:</strong> {formData.manufacturing_date}
            </div>
            <div>
              <strong>סוג מנוע:</strong> {input_data.engine_type}
            </div>
            <div>
              <strong>משקל רכב:</strong>{" "}
              {input_data.is_heavy_vehicle ? "מעל 3.5 טון" : "עד 3.5 טון"}
            </div>
            <div>
              <strong>ערך המונית:</strong> {formData.vehicle_price_foreign}{" "}
              {formData.currency_type}
            </div>
            <div>
              <strong>שער חליפין:</strong> {formData.exchange_rate}
            </div>
            <div>
              <strong>עלות הובלה:</strong>{" "}
              {formData.shipping_cost_foreign || "0"} {formData.currency_type}
            </div>
            <div>
              <strong>עלות ביטוח:</strong>{" "}
              {formData.insurance_cost_foreign || "0"}{" "}
              {formData.insurance_currency_type || formData.currency_type}
            </div>
            <div>
              <strong>הוצאות מקומיות:</strong>{" "}
              {formData.local_expenses_ils || "0"} ש"ח
            </div>
            <div>
              <strong>הצהרת מקור:</strong>{" "}
              {formData.has_origin_declaration ? "✅ יש" : "❌ אין"}
            </div>
            {!isHeavy && formData.green_rating_group && (
              <div>
                <strong>🌱 ציון ירוק:</strong> קבוצה{" "}
                {getGreenRatingGroupName(parseInt(formData.green_rating_group))}
              </div>
            )}
          </div>
        </MotionCard>

        {/* אינדיקטור רכב כבד/רגיל */}
        {isHeavy ? (
          <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
            <p className="text-orange-800 font-semibold text-center">
              🚛 רכב כבד מעל 3500 ק"ג - מס קניה 0%, ללא אבזור וציון ירוק
            </p>
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <p className="text-green-800 font-semibold text-center">
              רכב רגיל מתחת ל-3500 ק"ג - מס קניה 8% + אבזור בטיחותי וציון ירוק
            </p>
          </div>
        )}

        <MotionCard className="p-6">
          <h3 className="font-bold text-lg mb-6 text-center">
             תוצאות החישוב
          </h3>

          <div className="space-y-4 text-black">
            {/* 1. ערך לצרכי מכס */}
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="font-medium">1. ערך לצרכי מכס:</span>
              <span className="font-bold">
                {formatCurrency(
                  intermediate_calculations.total_value_for_customs
                )}{" "}
                ש"ח
              </span>
            </div>

            {/* 2. סך המכס */}
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="font-medium">2. סך המכס:</span>
              <span className="font-bold">
                {formatCurrency(intermediate_calculations.customs_fee)} ש"ח
                {intermediate_calculations.customs_fee === 0 && (
                  <span className="text-green-600 mr-2 text-sm">✓ פטור</span>
                )}
              </span>
            </div>

            {/* 3. שיעור המכס */}
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="font-medium">3. שיעור המכס:</span>
              <span className="font-bold">
                {intermediate_calculations.customs_rate_percent.toFixed(2)}%
              </span>
            </div>

            {/* 4. מס קניה לפני התאמות */}
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="font-medium">4. מס קניה לפני התאמות:</span>
              <span className="font-bold">
                {formatCurrency(
                  intermediate_calculations.purchase_tax_before_adjustments
                )}{" "}
                ש"ח
              </span>
            </div>

            {/* הנחות/תוספות - רק אם רכב רגיל */}
            {!isHeavy && (
              <>
                {/* 4א. ציון ירוק */}
                {intermediate_calculations.green_rating_info && (
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="font-medium">
                      4א.{" "}
                      {intermediate_calculations.green_rating_info.is_discount
                        ? "הנחת"
                        : "תוספת"}{" "}
                      ציון ירוק:
                    </span>
                    <span
                      className={`font-bold ${
                        intermediate_calculations.green_rating_info.is_discount
                          ? "text-green-700"
                          : "text-red-700"
                      }`}
                    >
                      {intermediate_calculations.green_rating_info.is_discount
                        ? "-"
                        : "+"}
                      {formatCurrency(
                        Math.abs(
                          intermediate_calculations.green_rating_info
                            .applied_adjustment
                        )
                      )}{" "}
                      ש"ח
                    </span>
                  </div>
                )}

                {/* 4ב. הנחת אבזור בטיחותי */}
                {safety_equipment && safety_equipment.discount_amount > 0 && (
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="font-medium">4ב. הנחת אבזור בטיחותי:</span>
                    <span className="font-bold text-green-700">
                      -{formatCurrency(safety_equipment.discount_amount)} ש"ח
                    </span>
                  </div>
                )}
              </>
            )}

            {/* 5. מס קניה סופי */}
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="font-medium">
                5. מס קניה סופי {!isHeavy && "(אחרי התאמות)"}:
              </span>
              <span className="font-bold text-green-700">
                {formatCurrency(final_results.purchase_tax_final)} ש"ח
              </span>
            </div>

            {/* 6. שיעור מס קניה */}
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="font-medium">6. שיעור מס הקניה:</span>
              <span className="font-bold">
                {(() => {
                  // חישוב שיעור מס קניה: (מס קניה סופי / בסיס מס קניה) × 100
                  const purchaseTaxRate =
                    intermediate_calculations.purchase_tax_base > 0
                      ? (final_results.purchase_tax_final /
                          intermediate_calculations.purchase_tax_base) *
                        100
                      : 0;
                  return purchaseTaxRate.toFixed(2);
                })()}
                %
              </span>
            </div>

            {/* 7. סך המע"מ */}
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="font-medium">7. סך המע"מ:</span>
              <span className="font-bold">
                {formatCurrency(final_results.vat)} ש"ח
              </span>
            </div>

            {/* 8. שיעור המע"מ */}
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="font-medium">8. שיעור המע"מ:</span>
              <span className="font-bold">18%</span>
            </div>

            {/* 9. סך המיסים */}
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="font-medium">9. סך המיסים:</span>
              <span className="font-bold">
                {formatCurrency(final_results.total_taxes)} ש"ח
              </span>
            </div>

            {/* 10. שיעור המיסים */}
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="font-medium">10. שיעור המיסים:</span>
              <span className="font-bold">
                {final_results.tax_percentage.toFixed(1)}%
              </span>
            </div>

            {/* 11. עלות הרכב כולל מיסים */}
            <div className="flex justify-between items-center py-3 border-t-2 border-gray-800 bg-gray-50 px-4 rounded-lg">
              <span className="font-bold text-lg">
                11. עלות הרכב כולל מיסים:
              </span>
              <span className="font-bold text-xl text-green-700">
                {formatCurrency(final_results.final_total)} ש"ח
              </span>
            </div>
          </div>
        </MotionCard>
        {!isHeavy && safety_equipment && safety_equipment.is_eligible && (
          <MotionCard className="p-6 bg-orange-50 border-2 border-orange-200">
            <h3 className="font-bold text-lg mb-4 text-orange-800">
              🛡️ פירוט אבזור בטיחותי M1
            </h3>

            <div className="space-y-2 text-sm mb-4">
              {/* כריות אוויר */}
              {safety_equipment.features.airbagsCount > 0 && (
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <span className="text-green-600">✅</span>
                  <span>
                    כריות אוויר: {safety_equipment.features.airbagsCount}
                  </span>
                </div>
              )}

              {/* מערכות בטיחות - עדכון נובמבר 2025 (תקנות 2026) */}
              {safety_equipment.features.systems.blindSpotDetection && (
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <span className="text-green-600">✅</span>
                  <span>זיהוי כלי רכב ב"שטח מת"</span>
                </div>
              )}
              {safety_equipment.features.systems
                .blindSpotCollisionPrevention && (
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <span className="text-green-600">✅</span>
                  <span>אקטיבית למניעת התנגשות צד ב"שטח מת"</span>
                </div>
              )}
              {safety_equipment.features.systems.reverseBraking && (
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <span className="text-green-600">✅</span>
                  <span>בלימה אוטומטית בנסיעה לאחור</span>
                </div>
              )}
              {safety_equipment.features.systems.driverDistraction && (
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <span className="text-green-600">✅</span>
                  <span>התרעה במצב היסח דעת הנהג</span>
                </div>
              )}
              {safety_equipment.features.systems.childAlert && (
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <span className="text-green-600">✅</span>
                  <span>התרעה על השארת ילדים ברכב</span>
                </div>
              )}
              {safety_equipment.features.systems.doorOpenWarning && (
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <span className="text-green-600">✅</span>
                  <span>אזהרת פתיחת דלת</span>
                </div>
              )}
              {safety_equipment.features.systems.adaptiveLighting && (
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <span className="text-green-600">✅</span>
                  <span>תאורה אדפטיבית/שליטה באורות גבוהים</span>
                </div>
              )}
              {safety_equipment.features.systems.junctionEmergencyBraking && (
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <span className="text-green-600">✅</span>
                  <span>בלימת חירום לתנועה חוצה בצמתים</span>
                </div>
              )}
            </div>

            <div className="mt-4 p-3 bg-orange-100 rounded-lg">
              <p className="text-sm text-orange-800">
                <strong>רמת אבזור:</strong> רמה {safety_equipment.safety_level}{" "}
                (ציון: {safety_equipment.safety_score.toFixed(1)})
              </p>
              <p className="text-sm text-orange-800 mt-1">
                <strong>הנחה שהתקבלה:</strong>{" "}
                {formatCurrency(safety_equipment.discount_amount)} ש"ח
                {safety_equipment.discount_amount >=
                  safety_equipment.max_discount_allowed && (
                  <span className="text-xs block mt-1">
                    (הגבלה: עד גובה מס הקניה)
                  </span>
                )}
              </p>
            </div>
          </MotionCard>
        )}

        {/* ============================================================ */}
        {/* דייק לי את התוצאה */}
        {/* ============================================================ */}
        {calculationResult && (
          <ActualCostCalculator
            mode="commercial"
            vehicleCurrency={formData.currency_type}
            vehicleExchangeRate={formData.exchange_rate}
            vehiclePriceForeign={formData.vehicle_price_foreign}
            shippingCostForeign={formData.shipping_cost_foreign}
            insuranceCurrency={formData.insurance_currency_type}
            insuranceExchangeRate={formData.insurance_exchange_rate}
            insuranceCostForeign={formData.insurance_cost_foreign}
            localExpensesIls={formData.local_expenses_ils}
            totalTaxes={calculationResult?.final_results?.total_taxes ?? 0}
            onDataChange={setCommercialActualCostData}
            explanations={explanations}
            initialData={commercialActualCostData}
          />
        )}
        </div>
        {/* סוף אזור ההדפסה */}

        {/* ============================================================ */}
        {/* שמירה והערות */}
        {/* ============================================================ */}
        <MotionCard className="p-6 print-exclude">
          <h3 className="font-bold text-lg mb-4 text-gray-800">
            💾 שמירת החישוב
          </h3>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              הערות נוספות (אופציונלי):
            </label>
            <textarea
              value={userNotes}
              onChange={(e) =>
                e.target.value.length <= 1000 && setUserNotes(e.target.value)
              }
              rows={4}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="הוסף הערות על החישוב, פרטים נוספים על המונית וכו'..."
            />
            <div className="text-sm text-gray-500 mt-1">
              {userNotes.length}/1000 תווים
            </div>
          </div>

          <div className="flex justify-between items-center mt-8">
            <MotionButton
              onClick={() => setCurrentStep(1)}
              variant="secondary"
              disabled={isSaving || isSaved}
              className="px-6 py-3"
            >
              ← ערוך נתונים
            </MotionButton>

            <MotionButton
              onClick={handlePrint}
              variant="secondary"
              disabled={isSaving || isSaved}
              className="px-6 py-3 bg-gray-100 hover:bg-gray-200"
            >
              🖨️ הדפס
            </MotionButton>

            <MotionButton
              onClick={handleSaveCalculation}
              disabled={isSaving || isSaved}
              className={`${
                isSaved
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-blue-600 hover:bg-blue-700"
              } text-white px-6 py-3`}
            >
              {isSaving
                ? "שומר..."
                : isSaved
                ? "נשמר ✓"
                : "שמור וחזור לדף הבית"}
            </MotionButton>
          </div>
        </MotionCard>
      </div>
    );
  };

  // ============================================================
  // Main Render
  // ============================================================

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" dir="rtl">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            מחשבון מוניות - ייבוא מסחרי
          </h1>
          <p className="text-gray-600">
            חישוב מיסים למוניות המיובאות באופן מסחרי
          </p>
        </div>

        {/* Progress */}
        <div className="flex justify-center items-center gap-4 mb-8">
          <div
            className={`flex items-center justify-center w-10 h-10 rounded-full ${
              currentStep >= 1
                ? "bg-blue-600 text-white"
                : "bg-gray-300 text-gray-600"
            } font-bold`}
          >
            1
          </div>
          <div
            className={`h-1 w-20 ${
              currentStep >= 2 ? "bg-blue-600" : "bg-gray-300"
            }`}
          />
          <div
            className={`flex items-center justify-center w-10 h-10 rounded-full ${
              currentStep >= 2
                ? "bg-blue-600 text-white"
                : "bg-gray-300 text-gray-600"
            } font-bold`}
          >
            2
          </div>
        </div>

        {/* Error Display */}
        {/* {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg mb-6">
            ❌ {error}
          </div>
        )} */}

        {/* Steps */}
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}

        {/* Footer Links */}
        <div className="text-center mt-8 pt-6 border-t border-gray-200">
          <div className="flex justify-center gap-6 text-sm text-gray-500">
            <Link
              to="/terms-and-privacy"
              className="hover:text-blue-600 transition-colors"
            >
              תנאי שימוש ופרטיות
            </Link>
            <span>|</span>
            <Link
              to="/accessibility-statement"
              className="hover:text-blue-600 transition-colors"
            >
              הצהרת נגישות
            </Link>
          </div>
        </div>
      </div>

      {/* Green Rating Modal */}
      {showGreenRatingModal && (
        <GreenRatingCalculatorModal
          isOpen={showGreenRatingModal}
          onSelectGroup={handleGreenRatingGroupSelect}
          onClose={() => setShowGreenRatingModal(false)}
          currentGroupNumber={
            formData.green_rating_group
              ? parseInt(formData.green_rating_group)
              : undefined
          }
        />
      )}
    </div>
  );
};

export default TaxiCommercialCalculatorPage;
